import { useState } from 'react';
import { Save, Loader2, Video, Mail, AlertTriangle, CheckCircle, Clock, X, KeyRound } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const IST_OFFSET_MIN = 330;

export const STEP_TITLES = ['Your Details', 'Language', 'Trading Setup', 'Platform', 'Timeline', 'Tester', 'Question'];
export const LANGUAGE_LABELS = { hindi: 'Hindi', english: 'English', both: 'Both (Hinglish)' };
export const FORM_STATUS_CONFIG = {
  Qualified: { color: '#10B981' },
  Exploring: { color: '#06b6d4' },
  Incomplete: { color: '#F59E0B' },
};
export const OUTCOME_LABELS = { interested: 'Joined, interested', not_now: 'Joined, not now', no_show: 'No-show' };
const TRIED_TESTER_LABELS = { yes_worked: 'Yes, it worked', couldnt_get_working: "Couldn't get it working", not_yet: 'Not yet' };
const TIMELINE_LABELS = { this_week: 'This week', this_month: 'This month', exploring: 'Just exploring' };

const toIstInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() + IST_OFFSET_MIN * 60000).toISOString().slice(0, 16);
};

export const formatIst = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-GB', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' }) + ' IST';
};

const formatInZone = (iso, zone) => {
  try {
    return new Date(iso).toLocaleString('en-GB', { timeZone: zone, dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return null;
  }
};

export const FormStatusBadge = ({ status, testId }) => {
  const cfg = FORM_STATUS_CONFIG[status];
  if (!cfg) return null;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ color: cfg.color, backgroundColor: `${cfg.color}20` }} data-testid={testId}>
      {status}
    </span>
  );
};

const EmailStatus = ({ lead }) => {
  const status = lead.email_status;
  if (status === 'sent') {
    return <span className="text-[#10B981] flex items-center gap-1" data-testid="lead-email-status"><CheckCircle size={12} /> Email sent{lead.email_sent_at ? ` · ${formatIst(lead.email_sent_at)}` : ''}</span>;
  }
  if (status === 'failed') {
    return (
      <span className="text-red-400 flex items-start gap-1" data-testid="lead-email-status">
        <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" /> Email failed: {lead.email_error || 'unknown error'}
      </span>
    );
  }
  if (status === 'sending') return <span className="text-white/60 flex items-center gap-1" data-testid="lead-email-status"><Clock size={12} /> Sending email…</span>;
  return <span className="text-white/40 flex items-center gap-1" data-testid="lead-email-status"><Mail size={12} /> No email sent</span>;
};

const Answer = ({ label, value }) => (
  <div>
    <span className="text-white/40">{label}:</span>
    <p className="text-white break-words">{value || '-'}</p>
  </div>
);

export const CallFormAnswers = ({ lead }) => {
  const capital = lead.capitalAbove3k === 'amount' ? `$${lead.capitalAmount}`
    : lead.capitalAbove3k === 'yes' ? 'Above $3k' : lead.capitalAbove3k === 'no' ? 'Below $3k' : null;
  const lastStep = lead.last_step ? `${lead.last_step} of 7 (${STEP_TITLES[lead.last_step - 1]})` : null;
  return (
    <div className="p-3 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20" data-testid="lead-call-form-answers">
      <div className="flex items-center justify-between mb-2">
        <h5 className="text-[#F59E0B] text-xs font-semibold">Call Request</h5>
        <FormStatusBadge status={lead.form_status} testId="lead-form-status" />
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Answer label="Call language" value={LANGUAGE_LABELS[lead.language] || lead.language} />
        <Answer label="Last step answered" value={lastStep} />
        <Answer label="Trading location" value={lead.tradingLocation} />
        <Answer label="Capital" value={capital} />
        <Answer label="Prop size" value={lead.propAccountSize} />
        <Answer label="Platform" value={lead.platform} />
        <Answer label="Timeline" value={TIMELINE_LABELS[lead.timeline] || lead.timeline} />
        <Answer label="Tried tester" value={TRIED_TESTER_LABELS[lead.triedTester] || lead.triedTester} />
      </div>
      <div className="mt-2 text-xs"><Answer label="Main question" value={lead.mainQuestion} /></div>
      <div className="mt-2 text-xs">{lead.whatsapp_opened ? <span className="text-[#25D366]">Tapped "Schedule on WhatsApp"</span> : null}</div>
      <div className="mt-2 text-xs"><EmailStatus lead={lead} /></div>
      {lead.auto_notes?.length > 0 && (
        <ul className="mt-2 text-xs text-[#F59E0B]">{lead.auto_notes.map((n, i) => <li key={i}>• {n}</li>)}</ul>
      )}
    </div>
  );
};

const putJson = async (url, body, authHeader) => {
  const res = await fetch(url, { method: 'PUT', headers: { ...authHeader, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || 'Save failed');
  }
  return res.json();
};

export const CallTimeField = ({ lead, authHeader, onSaved, compact = false }) => {
  const [value, setValue] = useState(toIstInput(lead.call_time));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    if (!value) return;
    setSaving(true); setError('');
    try {
      await putJson(`${API_URL}/api/admin/crm/leads/${lead._id}/call-time`, { call_time: `${value}:00+05:30` }, authHeader);
      onSaved?.();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };
  const leadLocal = lead.call_time && lead.timezone ? formatInZone(lead.call_time, lead.timezone) : null;
  return (
    <div data-testid={`call-time-field-${lead._id}`}>
      {!compact && <label className="text-white/40 text-xs mb-1 block">Call date and time (IST)</label>}
      <div className="flex gap-2">
        <input type="datetime-local" style={{ colorScheme: 'dark' }} value={value} onChange={(e) => setValue(e.target.value)}
          className={`${compact ? 'w-44 text-xs py-1.5' : 'flex-1 text-sm py-2'} px-3 rounded-lg bg-white/5 border border-white/10 text-white`}
          data-testid={`call-time-input-${lead._id}`} />
        <button onClick={save} disabled={!value || saving} className="px-3 rounded-lg bg-[#7c3aed] text-white text-xs font-medium disabled:opacity-40 flex items-center gap-1"
          data-testid={`call-time-save-${lead._id}`}>
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
        </button>
      </div>
      <p className="text-white/40 text-xs mt-1">
        Lead's time zone: <span className="text-white/70">{lead.timezone || 'unknown'}</span>
        {leadLocal && <span> · their time {leadLocal}</span>}
      </p>
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
};

export const MeetLinkField = ({ lead, authHeader, onSaved }) => {
  const [value, setValue] = useState(lead.meet_link || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setSaving(true); setError('');
    try {
      await putJson(`${API_URL}/api/admin/crm/leads/${lead._id}/meet-link`, { meet_link: value }, authHeader);
      onSaved?.();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };
  return (
    <div>
      <label className="text-white/40 text-xs mb-1 block flex items-center gap-1"><Video size={12} /> Google Meet link (for your reference)</label>
      <div className="flex gap-2">
        <input type="url" value={value} onChange={(e) => setValue(e.target.value)} placeholder="https://meet.google.com/..."
          className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" data-testid={`meet-link-input-${lead._id}`} />
        <button onClick={save} disabled={saving} className="px-3 rounded-lg bg-white/10 text-white text-xs font-medium flex items-center gap-1 hover:bg-white/20"
          data-testid={`meet-link-save-${lead._id}`}>
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
        </button>
      </div>
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
};

export const NotesField = ({ lead, authHeader, onSaved }) => {
  const [value, setValue] = useState(lead.manual_notes || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await putJson(`${API_URL}/api/admin/crm/leads/${lead._id}/notes`, { notes: value }, authHeader);
      onSaved?.();
    } finally { setSaving(false); }
  };
  return (
    <div>
      <label className="text-white/40 text-xs mb-1 block">Notes</label>
      <textarea value={value} onChange={(e) => setValue(e.target.value)} rows={3}
        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm resize-none" data-testid={`notes-input-${lead._id}`} />
      <button onClick={save} disabled={saving} className="mt-1 px-3 py-1.5 rounded-lg bg-white/10 text-white text-xs font-medium flex items-center gap-1 hover:bg-white/20"
        data-testid={`notes-save-${lead._id}`}>
        {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save notes
      </button>
    </div>
  );
};

export const OutcomeButtons = ({ lead, onSelect }) => (
  <div className="flex gap-2" data-testid={`outcome-buttons-${lead._id}`}>
    {Object.entries(OUTCOME_LABELS).map(([outcome, label]) => (
      <button key={outcome} onClick={() => onSelect(lead._id, outcome)}
        className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-colors ${
          lead.call_outcome === outcome
            ? outcome === 'interested' ? 'bg-[#10B981] text-white' : outcome === 'not_now' ? 'bg-[#06b6d4] text-white' : 'bg-[#F59E0B] text-white'
            : 'bg-white/10 text-white/60 hover:bg-white/20'
        }`}
        data-testid={`outcome-${outcome}-${lead._id}`}>
        {label}
      </button>
    ))}
  </div>
);

export const ChangePasswordModal = ({ authHeader, onClose, onChanged }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next !== confirm) { setError('New passwords do not match'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/change-password`, {
        method: 'POST', headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.detail || 'Could not change password'); return; }
      onChanged(next);
    } finally { setSaving(false); }
  };
  const field = 'w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#7c3aed]/50';
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70" data-testid="change-password-modal">
      <form onSubmit={submit} className="w-full max-w-sm glass rounded-2xl p-6 space-y-3 bg-[#0a0a12]">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-semibold text-white flex items-center gap-2"><KeyRound size={16} /> Change admin password</h3>
          <button type="button" onClick={onClose} data-testid="change-password-close"><X size={16} className="text-white/60" /></button>
        </div>
        <input type="password" className={field} placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="change-password-current" />
        <input type="password" className={field} placeholder="New password (12+ characters)" value={next} onChange={(e) => setNext(e.target.value)} required minLength={12} data-testid="change-password-new" />
        <input type="password" className={field} placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="change-password-confirm" />
        {error && <p className="text-red-400 text-xs" data-testid="change-password-error">{error}</p>}
        <button type="submit" disabled={saving} className="w-full btn-purple py-3 flex items-center justify-center gap-2" data-testid="change-password-submit">
          {saving ? <Loader2 size={16} className="animate-spin" /> : 'Update password'}
        </button>
      </form>
    </div>
  );
};
